import { motion } from 'framer-motion';
import { CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { CartItem } from '../contexts/CartContext';
import type { FormData } from '../pages/CheckoutPage';

interface OrderConfirmationProps {
  formData: FormData;
  cartItems: CartItem[];
  calculateTotals: () => { subtotal: number; total: number };
}

export function OrderConfirmation({ formData, cartItems, calculateTotals }: OrderConfirmationProps) {
  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="bg-white rounded-2xl shadow-lg p-8"
      >
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle className="w-8 h-8 text-green-600" />
        </div>
        
        <h2 className="text-2xl font-bold text-gray-900 text-center mb-4">Order Placed Successfully!</h2>
        <p className="text-gray-600 text-center mb-8">Thank you for your order. We will get back to you shortly.</p>
        
        <div className="bg-gray-50 rounded-lg p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left mb-6">
            <div>
              <p className="text-sm text-gray-500">Order ID</p>
              <p className="font-semibold">#RAP{Date.now().toString().slice(-6)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Scheduled Date</p>
              <p className="font-semibold">{formData.preferredDate} at {formData.preferredTime}</p>
            </div>
          </div>

          <div className="border-t border-gray-200 pt-4">
            <h3 className="text-lg font-semibold mb-4">Service Details</h3>
            <div className="space-y-4">
              {cartItems.map((item) => (
                <div key={item.id} className="space-y-2">
                  <div className="flex justify-between">
                    <div>
                      <p className="font-medium">{item.name}</p>
                      <p className="text-sm text-gray-600">Qty: {item.quantity}</p>
                    </div>
                    <p className="font-medium">₹{(item.price * item.quantity).toLocaleString()}</p>
                  </div>
                  {item.addons && item.addons.length > 0 && (
                    <div className="ml-4 space-y-1">
                      {item.addons.map((addon) => (
                        <div key={addon.id} className="flex justify-between text-sm">
                          <p className="text-gray-600">+ {addon.name}</p>
                          <p className="text-gray-600">₹{(addon.price * item.quantity).toLocaleString()}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="border-t border-gray-200 mt-6 pt-4">
            <div className="space-y-2">
              {(() => {
                const { subtotal, total } = calculateTotals();
                return (
                  <>
                    <div className="flex justify-between text-gray-600">
                      <span>Subtotal</span>
                      <span>₹{subtotal.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-lg font-bold border-t border-gray-200 pt-2">
                      <span>Total Amount</span>
                      <span>₹{total.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-sm mt-2">
                      <span className="text-gray-500">Payment Status</span>
                      <span className="font-medium text-green-600">
                        {formData.paymentMethod === 'cod' ? 'Pending' : 'Paid'}
                      </span>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        </div>
        
        <div className="flex flex-col items-center space-y-4">
          <Link
            to="/services"
            className="bg-primary-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-primary-700 transition-colors"
          >
            Book More Services
          </Link>
          <Link
            to="/account"
            className="text-primary-600 hover:text-primary-700 font-medium"
          >
            View Order Details
          </Link>
        </div>
      </motion.div>
    </div>
  );
}
